
import { Component, ChangeDetectionStrategy, input, computed, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Correction } from '../../models/correction.model';

@Component({
  selector: 'app-evolution-tracker',
  standalone: true,
  template: `


<div class="bg-white p-5 rounded-2xl shadow-card border border-slate-200/80 mb-6 animate-fade-in-content" [class.hidden]="history().length === 0">
  <div class="flex flex-col sm:flex-row justify-between sm:items-center mb-4">
    <h2 class="text-sm font-bold tracking-tight text-slate-900">Sua Evolução Cognitiva</h2>
    @if(history().length >= 3){
      <button (click)="onGeneratePlan()" class="btn-primary mt-3 sm:mt-0 flex items-center py-2 px-3.5 text-xs">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 mr-1.5">
          <path fill-rule="evenodd" d="M15.988 3.012A2.25 2.25 0 0 1 18 5.25v6.5A2.25 2.25 0 0 1 15.75 14h-3.04a.75.75 0 0 0-.52.212L9 17.612l-3.19-3.4a.75.75 0 0 0-.52-.212H2.25A2.25 2.25 0 0 1 0 11.75v-6.5A2.25 2.25 0 0 1 2.25 3h11.527c.18 0 .355.018.52.053l1.691-.041Z" clip-rule="evenodd" />
          <path d="M9.057 3.22c.13.02.26.047.388.082a.75.75 0 0 1 .326 1.332l-2.23 1.114A.75.75 0 0 1 6.75 5.5v.193l-1.6 1.6A.75.75 0 0 1 4.5 6.5v-1a.75.75 0 0 1 .447-.691l4.11-2.056a.75.75 0 0 1 .557.158Z" />
        </svg>
        Gerar Plano de Evolução
      </button>
    }
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
    <div class="space-y-3">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 bg-indigo-50 dark:bg-indigo-950/40 text-custom-blue rounded-lg flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" /></svg>
        </div>
        <div>
          <p class="text-xs text-gray-500 dark:text-gray-400 font-medium">Média Cognitiva Geral</p>
          <p class="text-2xl font-bold text-custom-black dark:text-white">{{ averageCognitiveScore().toFixed(1) }}</p>
        </div>
      </div>
      @if(history().length >= 2) {
        <div class="flex items-center gap-3">
          @switch(trend().status) {
            @case('improving') {
              <div class="w-10 h-10 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 rounded-lg flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18 9 11.25l3-3L21.75 12" /></svg></div>
              <div><p class="text-emerald-600 dark:text-emerald-400 text-xs font-semibold">Tendência de Melhora</p><p class="text-xs text-gray-500 dark:text-gray-400">Sua nota média geral está subindo</p></div>
            }
            @case('declining') {
              <div class="w-10 h-10 bg-red-50 dark:bg-red-950/30 text-red-500 rounded-lg flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6 9 12.75l3 3L21.75 12" /></svg></div>
              <div><p class="text-red-500 text-xs font-semibold">Tendência de Queda</p><p class="text-xs text-gray-500 dark:text-gray-400">Sua nota média geral está caindo</p></div>
            }
            @case('stable') {
              <div class="w-10 h-10 bg-gray-50 dark:bg-gray-700/50 text-gray-500 rounded-lg flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 9h16.5" /></svg></div>
              <div><p class="text-gray-600 dark:text-gray-300 text-xs font-semibold">Estável</p><p class="text-xs text-gray-500 dark:text-gray-400">Mantenha a consistência nos estudos</p></div>
            }
          }
        </div>
      }
    </div>

    @if(history().length >= 2) {
      <div class="bg-custom-gray dark:bg-gray-700/30 p-3.5 rounded-lg">
        <h3 class="font-medium text-custom-black dark:text-white mb-2 text-xs">Progresso por Habilidade</h3>
        <div class="space-y-1.5">
           @for(cat of categoryTrends(); track cat.name) {
            <div class="flex items-center justify-between text-xs">
              <span class="text-gray-600 dark:text-gray-300">{{ cat.name }}</span>
              <div class="flex items-center">
                @if (cat.trend > 0.1) {
                  <span class="font-bold text-emerald-600">+{{ cat.trend.toFixed(1) }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5 text-emerald-500 ml-1"><path fill-rule="evenodd" d="M8 15a.75.75 0 0 1-.75-.75V2.56L4.03 5.78a.75.75 0 0 1-1.06-1.06l4.25-4.25a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1-1.06 1.06L8.75 2.56v11.69a.75.75 0 0 1-.75.75Z" clip-rule="evenodd" /></svg>
                } @else if (cat.trend < -0.1) {
                  <span class="font-bold text-red-500">{{ cat.trend.toFixed(1) }}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5 text-red-500 ml-1"><path fill-rule="evenodd" d="M8 1a.75.75 0 0 1 .75.75v11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L4.22 11.28a.75.75 0 1 1 1.06-1.06l3.22 3.22V1.75A.75.75 0 0 1 8 1Z" clip-rule="evenodd" /></svg>
                } @else {
                  <span class="font-bold text-gray-500">~0.0</span>
                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-3.5 h-3.5 text-gray-400 ml-1"><path d="M4.505 7.655a.75.75 0 0 0 0 1.06l2.25 2.25a.75.75 0 0 0 1.06 0l2.25-2.25a.75.75 0 0 0-1.06-1.06L8.5 8.19V1.75a.75.75 0 0 0-1.5 0v6.44L5.565 7.655a.75.75 0 0 0-1.06 0Z" /><path d="M1.75 9.5a.75.75 0 0 0 0 1.5h12.5a.75.75 0 0 0 0-1.5H1.75Z" /></svg>
                }
              </div>
            </div>
           }
        </div>
      </div>
    }
  </div>
</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule]
})
export class EvolutionTrackerComponent {
  history = input.required<Correction[]>();
  generatePlanRequest = output<void>();

  readonly averageCognitiveScore = computed(() => {
    const history = this.history();
    if (history.length === 0) return 0;
    
    const totalScore = history.reduce((sum, correction) => {
      const diagnosis = correction.diagnosticoCognitivo || [];
      const correctionAvg = diagnosis.reduce((diagSum, item) => diagSum + item.nota, 0) / (diagnosis.length || 1);
      return sum + correctionAvg;
    }, 0);
    
    return totalScore / history.length;
  });
  
  readonly trend = computed(() => {
    const history = this.history();
    // Require at least 4 data points for a meaningful trend by comparing halves
    if (history.length < 4) {
      return { status: 'stable', change: 0 };
    }
    
    // History is newest first, so reverse for chronological order
    const chronologicalHistory = [...history].reverse();
    const halfIndex = Math.ceil(chronologicalHistory.length / 2);
    
    const firstHalf = chronologicalHistory.slice(0, halfIndex);
    const secondHalf = chronologicalHistory.slice(halfIndex);

    const avgFirstHalf = this.calculateAverageScoreForHistory(firstHalf);
    const avgSecondHalf = this.calculateAverageScoreForHistory(secondHalf);
    
    const change = avgSecondHalf - avgFirstHalf;
    
    let status: 'improving' | 'declining' | 'stable' = 'stable';
    if (change > 0.2) status = 'improving';
    if (change < -0.2) status = 'declining';
    
    return { status, change };
  });

  readonly categoryTrends = computed(() => {
    const history = this.history();
    if (history.length < 4) return [];

    const chronologicalHistory = [...history].reverse();
    const halfIndex = Math.ceil(chronologicalHistory.length / 2);
    const firstHalf = chronologicalHistory.slice(0, halfIndex);
    const secondHalf = chronologicalHistory.slice(halfIndex);
    
    const initialCategories = firstHalf[0].diagnosticoCognitivo.map(c => c.nome) || [];
    
    return initialCategories.map(categoryName => {
        const avgFirst = this.getAverageForCategory(firstHalf, categoryName);
        const avgSecond = this.getAverageForCategory(secondHalf, categoryName);
        const trend = avgSecond - avgFirst;
        return { name: categoryName, trend };
    }).sort((a, b) => b.trend - a.trend); // Sort by most improved
  });


  private calculateAverageScoreForHistory(corrections: Correction[]): number {
    if (corrections.length === 0) return 0;
    const totalScore = corrections.reduce((sum, c) => sum + this.getAverageScoreForCorrection(c), 0);
    return totalScore / corrections.length;
  }
  
  private getAverageScoreForCorrection(correction: Correction): number {
    const diagnosis = correction.diagnosticoCognitivo || [];
    if (diagnosis.length === 0) return 0;
    return diagnosis.reduce((sum, item) => sum + item.nota, 0) / diagnosis.length;
  }

  private getAverageForCategory(corrections: Correction[], categoryName: string): number {
    let total = 0;
    let count = 0;
    for (const correction of corrections) {
      const category = correction.diagnosticoCognitivo.find(c => c.nome === categoryName);
      if (category) {
        total += category.nota;
        count++;
      }
    }
    return count > 0 ? total / count : 0;
  }
  
  onGeneratePlan() {
    this.generatePlanRequest.emit();
  }
}
